<template>
  <span :class="generateStyles">
    {{ getTimestampValue }}
  </span>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { mapState } from 'vuex';
import UserStateMixin from 'client/components/mixins/UserStateMixin';
import moment from 'moment';

const { dateFormat, dataNotAvailable, neverExpiresText } = Constants.default;
const { formattedDateTypes } = Constants.BodyComponentConsts;

const getStylesByType = {};
getStylesByType[formattedDateTypes.idsourceLastContactField] = 'defaultStyles';
getStylesByType[formattedDateTypes.systemCreated] = 'lightStyles';
getStylesByType[formattedDateTypes.systemLastContact] = 'lightStyles';
getStylesByType[formattedDateTypes.userPasswordExpiry] = 'passwordStyles';
getStylesByType[formattedDateTypes.workdayTimestampField] = 'defaultStyles';

const getValueByType = {};
getValueByType[formattedDateTypes.idsourceLastContactField] = 'getIdsourceLastContact';
getValueByType[formattedDateTypes.systemCreated] = 'getDateFromTimestamp';
getValueByType[formattedDateTypes.systemLastContact] = 'getSystemLastContact';
getValueByType[formattedDateTypes.userPasswordExpiry] = 'getUserPasswordExpiry';
getValueByType[formattedDateTypes.workdayTimestampField] = 'getDateFromTimestamp';

/**
 * @todo Consider parameterizing this component so that the item field, moment
 * format, and class names can be passed as props by the caller. By doing this
 * developers would no longer need to add a new field to the formattedDateTypes
 * map and new getValueByType method each time a new item type was displayed
 * using this component.
 */
export default {
  name: 'FormattedDate',

  mixins: [UserStateMixin],

  props: {
    /**
     * format that informs moment.js how to format the timestamp.
     */
    dateFormat: {
      type: String,
      default: dateFormat,
    },
    /**
     * the data passed down to body components during rendering of the ItemTable row.
     */
    item: {
      required: true,
      type: Object,
    },

    type: {
      required: true,
      type: String,
      validator: function validator(value) {
        return !!formattedDateTypes[value];
      },
    },
  },

  computed: {
    /**
     * Passed from settings and used to determine user password expiry text
     */
    ...mapState('SettingsModule', ['passwordPolicy']),

    /**
     * Determines which method to use to compute the style of the component. This is nessesary
     * because some styles depend on other variables found in the item props.
     * @return {String} [class name that will go on the span that houses the
     *                  timestamp]
     */
    generateStyles() {
      return this[getStylesByType[this.type]]();
    },

    /**
     * formatTimestamp determines which key in the item prop to look for in order
     * to retrieve the timestamp. If there is not one then return an empty default
     * @return {String} [the formatted timestamp]
     */
    getTimestampValue() {
      return this[getValueByType[this.type]]();
    },
  },

  methods: {
    defaultStyles() {
      return this.$style.defaultStyles;
    },

    formatTimestamp(timestampValue) {
      return timestampValue
        ? moment(timestampValue).format(this.dateFormat)
        : dataNotAvailable;
    },

    getDateFromTimestamp() {
      return this.formatTimestamp(this.item.created);
    },

    getIdsourceLastContact() {
      const { lastUpdateTime } = this.item;
      return lastUpdateTime ? moment(lastUpdateTime).utc().fromNow() : dataNotAvailable;
    },

    getSystemLastContact() {
      const { lastContact } = this.item;
      return lastContact
        ? moment(lastContact).utc().fromNow()
        : dataNotAvailable;
    },

    getUserPasswordExpiry() {
      const { item } = this;
      if (item.password_never_expires && this.passwordPolicy.enablePasswordExpirationInDays) {
        return neverExpiresText;
      }
      if (item.externally_managed) {
        return dataNotAvailable;
      }
      if (!item.activated) {
        return dataNotAvailable;
      }
      return this.formatTimestamp(item.password_expiration_date);
    },

    lightStyles() {
      return this.$style.lightStyles;
    },

    passwordStyles() {
      const { item } = this;
      const classes = [this.$style.lightStyles];
      if (!item.password_expiration_date || item.externally_managed) {
        return classes;
      }

      if (item.password_expired || item.account_locked) {
        classes.push(this.$style.expired);
      }

      return classes;
    },
  },
};
</script>
<style scoped module>
@value (errorRed, greyDark3, jcOrange) from '~css/colors.css';

.defaultStyles {
  color: greyDark3;
  font-weight: 400;
}

.lightStyles {
  color: greyDark3;
  font-weight: 300;
}

.expired { color: errorRed; }
.warning { color: jcOrange; }
</style>
